import React, { useEffect, useState } from "react";
import { Landmark, Pencil, Plus, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { name: "", bank_name: "", iban: "", currency: "TRY", account_type: "vadesiz", balance: "" };

export default function Banks() {
  const { t, fmt } = useI18n();
  const { canEdit } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = () => api.get("/bank-accounts").then((r) => setRows(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setError(""); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r, balance: "" }); setError(""); setOpen(true); };

  const save = async () => {
    const payload = { ...form, balance: parseFloat(form.balance || 0) || 0 };
    try {
      if (editing) await api.put(`/bank-accounts/${editing.id}`, payload);
      else await api.post("/bank-accounts", payload);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/bank-accounts/${id}`);
    load().catch(() => {});
  };

  return (
    <div className="space-y-5" data-testid="banks-page">
      <div className="flex justify-end">
        {canEdit && (
          <Button onClick={openNew} data-testid="bank-new-button">
            <Plus className="mr-2 h-4 w-4" />{t.banksP.new}
          </Button>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
        {rows.length === 0 && (
          <p className="col-span-full rounded-2xl border border-dashed border-slate-200 py-16 text-center text-sm text-slate-400">{t.app.noData}</p>
        )}
        {rows.map((b) => (
          <div key={b.id} data-testid={`bank-card-${b.id}`}
            className="group flex flex-col rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm transition-transform duration-200 hover:-translate-y-0.5">
            <div className="flex items-start justify-between">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-900">
                <Landmark className="h-5 w-5 text-white" />
              </div>
              {canEdit && (
                <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => openEdit(b)} aria-label={t.app.edit} data-testid={`bank-edit-${b.id}`}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(b.id)} aria-label={t.app.delete} data-testid={`bank-delete-${b.id}`}
                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <p className="mt-5 font-display text-base font-semibold tracking-tight text-slate-900">{b.name}</p>
            <p className="mt-0.5 text-xs text-slate-500">{b.bank_name} · {b.account_type}</p>
            <p className="tnum mt-5 font-display text-2xl font-semibold tracking-tight text-slate-900">{fmt(b.balance, b.currency)}</p>
            {b.iban && <p className="mt-2 font-mono text-[10px] tracking-wide text-slate-400">{b.iban}</p>}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="bank-dialog">
          <DialogHeader><DialogTitle>{editing ? t.banksP.edit : t.banksP.new}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.banksP.accountName}</Label>
                <Input value={form.name} onChange={set("name")} data-testid="bank-name-input" />
              </div>
              <div className="space-y-2">
                <Label>{t.banksP.bankName}</Label>
                <Input value={form.bank_name} onChange={set("bank_name")} data-testid="bank-bankname-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t.banksP.iban}</Label>
              <Input value={form.iban || ""} onChange={set("iban")} className="font-mono text-xs" data-testid="bank-iban-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t.banksP.currency}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })} data-testid="bank-currency-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["TRY", "USD", "EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.banksP.type}</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })} data-testid="bank-type-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vadesiz">{t.app.pending === "Bekliyor" ? "Vadesiz" : "Checking"}</SelectItem>
                    <SelectItem value="vadeli">{t.app.pending === "Bekliyor" ? "Vadeli" : "Savings"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{editing ? "—" : t.banksP.balance}</Label>
                <Input type="number" step="0.01" value={form.balance} onChange={set("balance")} disabled={!!editing} data-testid="bank-balance-input" />
              </div>
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="bank-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="bank-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.name || !form.bank_name} data-testid="bank-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
